import type { FETCH_TRANSFORMER_ERROR_CODE } from 'utils';

export type FetchErrorCodeType = (typeof FETCH_TRANSFORMER_ERROR_CODE)[keyof typeof FETCH_TRANSFORMER_ERROR_CODE];

/**
 * Standardized error object for service responses
 */
export interface StandardErrorObject {
  code: string;
  psiCode?: number;
  message: string;
  trace: Record<string, unknown>;
  statusCode?: number;
  data?: Record<string, unknown>; // Field-level validation errors from PSI (for PsiError)
}

/**
 * Base error class for Fetch service operations
 *
 * Provides structured error information with:
 * - Error code (for programmatic handling)
 * - HTTP status code (when applicable)
 * - Additional context data
 * - Original error (for error chaining)
 *
 * @example
 * ```typescript
 * import { FETCH_TRANSFORMER_ERROR_CODE } from 'utils';
 *
 * throw new FetchError(
 *   'Failed to fetch data',
 *   FETCH_TRANSFORMER_ERROR_CODE.HTTP_ERROR,
 *   500,
 *   { url: 'https://api.example.com', method: 'POST' },
 *   originalError
 * );
 * ```
 */
export class FetchError extends Error {
  readonly type: string = 'FetchError';

  /**
   * @param message - Human-readable error message
   * @param code - Error code from FETCH_TRANSFORMER_ERROR_CODE constants
   * @param statusCode - HTTP status code (when applicable)
   * @param details - Additional context data (url, params, etc.)
   * @param cause - Original error that caused this error (error chaining)
   */
  constructor(
    message: string,
    public readonly code: FetchErrorCodeType,
    public readonly statusCode?: number,
    public readonly details?: Record<string, unknown>,
    public readonly cause?: Error,
  ) {
    super(message);

    // Maintains proper stack trace for where our error was thrown (V8 engines only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Get structured stack trace information
   *
   * Provides rich error context for debugging and logging.
   * Use this instead of manually building debug objects.
   *
   * @returns Structured object with error details, stack, and cause chain
   *
   * @example
   * ```typescript
   * catch (error) {
   *   logger.error({ stackTrace: error?.stackTrace });
   *   return { success: false, debug: error?.stackTrace };
   * }
   * ```
   */
  get stackTrace(): Record<string, unknown> {
    return this.toJSON();
  }

  /**
   * Returns standardized error object for service responses
   *
   * Provides unified error structure with all relevant fields.
   * Fields not applicable to the error type are set to undefined.
   *
   * @returns Standardized error object with code, psiCode, message, trace, statusCode
   *
   * @example
   * ```typescript
   * catch (error) {
   *   return { success: false, error: (error as ServiceError).toErrorObject() };
   * }
   * ```
   */
  toErrorObject(): StandardErrorObject {
    return {
      code: this.code,
      psiCode: undefined, // Base class doesn't have psiCode
      message: this.message,
      trace: this.stackTrace,
      statusCode: this.statusCode,
    };
  }

  /**
   * Convert error to JSON for logging
   */
  toJSON(): Record<string, unknown> {
    return {
      type: this.type,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: this.details,
      stack: this.stack,
      cause: this.cause
        ? {
            name: this.cause.name,
            message: this.cause.message,
            stack: this.cause.stack,
          }
        : undefined,
    };
  }
}
